import React, { useState } from 'react';

interface SelectOption {
  value: string;
  label: string;
}

interface FormSelectProps extends React.SelectHTMLAttributes<HTMLSelectElement> {
  label?: string;
  error?: string;
  helperText?: string;
  options: SelectOption[];
  placeholder?: string;
  className?: string;
  icon?: React.ReactNode;
}

const FormSelect: React.FC<FormSelectProps> = ({
  label,
  error,
  helperText,
  options,
  placeholder,
  className = '',
  icon,
  id,
  ...props
}) => {
  const [isFocused, setIsFocused] = useState(false);
  const selectId = id || `select-${Math.random().toString(36).substr(2, 9)}`;

  return (
    <div className={`group ${className}`}>
      {label && (
        <label
          htmlFor={selectId}
          className="block text-sm font-semibold text-neutral-700 mb-3 transition-colors duration-200 group-focus-within:text-primary-600"
        >
          {label}
          {props.required && <span className="text-red-500 ml-1">*</span>}
        </label>
      )}
      
      <div className="relative">
        {/* Left Icon */}
        {icon && (
          <div className="absolute left-4 top-1/2 transform -translate-y-1/2 text-neutral-400 group-focus-within:text-primary-500 transition-colors duration-200 z-10">
            {icon}
          </div>
        )}

        <select
          id={selectId}
          className={`
            w-full px-4 py-4 
            ${icon ? 'pl-12' : ''}
            pr-12
            bg-white border-2 rounded-2xl 
            text-neutral-900
            appearance-none cursor-pointer
            transition-all duration-200 ease-out
            focus:outline-none focus:ring-4 focus:ring-primary-500/20
            ${error
              ? 'border-red-300 focus:border-red-500 bg-red-50/50'
              : isFocused
              ? 'border-primary-500 shadow-glow'
              : 'border-neutral-200 hover:border-neutral-300'
            }
            ${props.disabled ? 'bg-neutral-50 cursor-not-allowed opacity-60' : ''}
          `.replace(/\s+/g, ' ').trim()}
          onFocus={(e) => {
            setIsFocused(true);
            props.onFocus?.(e);
          }}
          onBlur={(e) => {
            setIsFocused(false);
            props.onBlur?.(e);
          }}
          {...props}
        >
          {placeholder && (
            <option value="" disabled className="text-neutral-400">
              {placeholder}
            </option>
          )}
          {options.map((option) => (
            <option key={option.value} value={option.value} className="text-neutral-900">
              {option.label}
            </option>
          ))}
        </select>

        {/* Custom Dropdown Arrow */}
        <div className="absolute right-4 top-1/2 transform -translate-y-1/2 pointer-events-none">
          <svg 
            className={`w-5 h-5 transition-all duration-200 ${
              isFocused ? 'text-primary-500 rotate-180' : 'text-neutral-400'
            }`} 
            fill="none" 
            stroke="currentColor" 
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>

        {/* Focus ring animation */}
        {isFocused && !error && (
          <div className="absolute inset-0 rounded-2xl border-2 border-primary-500 animate-pulse-soft pointer-events-none" />
        )}
      </div>
      
      {/* Error Message */}
      {error && (
        <div className="mt-2 flex items-center gap-2 text-red-600 animate-fade-in-up">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-sm font-medium">{error}</p>
        </div>
      )}
      
      {/* Helper Text */}
      {helperText && !error && (
        <p className="mt-2 text-sm text-neutral-500 flex items-center gap-2">
          <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          {helperText}
        </p>
      )}
    </div>
  );
};

export default FormSelect;